import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "../config/api";

/**
 * usePremarketLevels(ticker)
 * --------------------------
 * Shared hook that fetches today's premarket high / low / open / prev-close
 * from /api/stock/premarket-snapshot/{ticker}. Refreshes every 60s so charts
 * stay in sync with the actual session.
 *
 * Returns:
 *   { ready, premarketHigh, premarketLow, previousClose, highTime, lowTime }
 *
 * Designed so every intraday chart can drop in two horizontal "ORB" reference
 * lines (PM High & PM Low) without duplicating fetch logic. Single source of
 * truth — and the localStorage fallback survives mobile network drops.
 */
export function usePremarketLevels(ticker) {
  const [snap, setSnap] = useState(null);
  const tickerRef = useRef(ticker);
  tickerRef.current = ticker;

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    const lsKey = `pm_levels_${ticker.toUpperCase()}`;

    // Seed from localStorage so charts can render PM lines instantly while
    // the fresh fetch is in flight.
    try {
      const cached = localStorage.getItem(lsKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.date === new Date().toISOString().slice(0, 10)) {
          setSnap(parsed.data);
        }
      }
    } catch (_) { /* ignore */ }

    const fetchOnce = async () => {
      try {
        const r = await axios.get(
          `${API}/stock/premarket-snapshot/${ticker}`,
          { timeout: 12000 }
        );
        if (cancelled || tickerRef.current !== ticker) return;
        if (r.data?.ready) {
          setSnap(r.data);
          try {
            localStorage.setItem(lsKey, JSON.stringify({
              date: new Date().toISOString().slice(0, 10),
              data: r.data,
            }));
          } catch (_) { /* ignore */ }
        }
      } catch (_) { /* swallow — chart still works without PM lines */ }
    };

    fetchOnce();
    const id = setInterval(fetchOnce, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [ticker]);

  return {
    ready: Boolean(snap?.ready),
    premarketHigh: snap?.premarket_high ?? null,
    premarketLow: snap?.premarket_low ?? null,
    previousClose: snap?.previous_close ?? null,
    highTime: snap?.high_time ?? null,
    lowTime: snap?.low_time ?? null,
  };
}

export default usePremarketLevels;
